// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { PayerOption } from '@polarissleep/shared';
import { normalizePayerSearch } from '@polarissleep/shared';

/**
 * One payer Organization a plan choice can resolve to. Payers with a Stedi payer ID are
 * matched on that ID; the one without (BCBS Other) is matched on its exact name and
 * must have no Stedi ID, so a renamed or look-alike Organization is never picked.
 */
export interface PlanPayer {
  key: string;
  label: string;
  /** Organization.name, also the search query sent to `search-insurance-payers`. */
  name: string;
  stediPayerId?: string;
}

export interface PlanOption {
  value: 'aetna' | 'bcbs' | 'uhc' | 'other';
  label: string;
  /** Empty for "Other plan (not listed)". More than one asks the patient which. */
  payers: readonly PlanPayer[];
}

export type PlanChoice = PlanOption['value'];

export const OTHER_PLAN: PlanChoice = 'other';

export const PLAN_OPTIONS: readonly PlanOption[] = [
  {
    value: 'aetna',
    label: 'Aetna',
    payers: [{ key: 'aetna', label: 'Aetna', name: 'Aetna', stediPayerId: '60054' }],
  },
  {
    value: 'bcbs',
    label: 'Blue Cross Blue Shield',
    payers: [
      {
        key: 'carefirst-md',
        label: 'CareFirst Blue Cross Blue Shield Maryland',
        name: 'CareFirst Blue Cross Blue Shield Maryland',
        stediPayerId: '00580',
      },
      {
        key: 'carefirst-dc',
        label: 'CareFirst Blue Cross Blue Shield D.C.',
        name: 'CareFirst BlueCross BlueShield District of Columbia',
        stediPayerId: 'SB580',
      },
      {
        key: 'carefirst-medicare',
        label: 'CareFirst Blue Cross Blue Shield Medicare Advantage',
        name: 'CareFirst BlueCross BlueShield Medicare Advantage',
        stediPayerId: '45282',
      },
      { key: 'bcbs-other', label: 'Another Blue Cross Blue Shield plan', name: 'Blue Cross Blue Shield (Other)' },
    ],
  },
  {
    value: 'uhc',
    label: 'United Healthcare',
    payers: [{ key: 'uhc', label: 'United Healthcare', name: 'United Healthcare', stediPayerId: '87726' }],
  },
  { value: OTHER_PLAN, label: 'Other plan (not listed)', payers: [] },
];

export function getPlanOption(value: PlanChoice | undefined): PlanOption | undefined {
  return PLAN_OPTIONS.find((option) => option.value === value);
}

export function getPlanPayer(plan: PlanChoice | undefined, key: string | undefined): PlanPayer | undefined {
  const payers = getPlanOption(plan)?.payers ?? [];
  return payers.length === 1 ? payers[0] : payers.find((payer) => payer.key === key);
}

function matchesPayer(option: PayerOption, payer: PlanPayer): boolean {
  if (payer.stediPayerId) {
    return option.stediPayerId === payer.stediPayerId;
  }
  return !option.stediPayerId && normalizePayerSearch(option.display) === normalizePayerSearch(payer.name);
}

/** Picks the one Organization for a plan payer, or throws if there is none or more than one. */
export function selectPlanPayer(options: PayerOption[], payer: PlanPayer): PayerOption {
  const matches = options.filter((option) => matchesPayer(option, payer));
  if (matches.length !== 1) {
    throw new Error(`Expected one payer Organization for ${payer.key}, found ${matches.length}`);
  }
  return matches[0];
}
